<template>
    <NuxtImg
        v-if="image && image.url"
        provider="prismic"
        :src="optimizedUrl"
        :loading="loading ?? 'lazy'"
        :preload="preloadWithHighPriority ? {fetchPriority: 'high'} : undefined"
        :alt="image.alt ?? image.url"
        :sizes="sizes"
        :height="height || image.dimensions.height"
        :width="width || image.dimensions.width"
        :densities="isSvg ? undefined : 'x1 x2'"
    />
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {NuxtImg} from '#components';

    interface Props {
        loading?: 'eager' | 'lazy';
        sizes?: string;
        image?: Record<string, any>;
        height?: string;
        width?: string;
    }

    const props = defineProps<Props>();

    const isSvg = computed(() => /\.svg(?:$|\?)/i.test(props.image?.url ?? ''));

    const optimizedUrl = computed(() => {
        const image = props.image;
        if (!image) {
            return '';
        }
        const {url} = image;
        try {
            const optimizedUrl = new URL(url);
            // SVGs are vector — CDN format conversion and pixel dimensions do not shrink them
            // and can waste bandwidth vs serving the raw asset.
            if (!isSvg.value) {
                optimizedUrl.searchParams.set('auto', 'enhanced,format');
                optimizedUrl.searchParams.set('w', props.width || image.dimensions.width);
                optimizedUrl.searchParams.set('h', props.height || image.dimensions.height);
            }
            return optimizedUrl.toString();
        } catch {
            return url;
        }
    });

    // Only when the caller mounts this with loading="eager" (e.g. visible hero on desktop).
    // Callers must not mount / must use lazy when the image is not displayed.
    const preloadWithHighPriority = computed((): boolean => {
        return props.loading === 'eager';
    });
</script>
